import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from "../context/CartContext";
import { useShop } from '../context/ShopContext';

import {
  User,
  Heart,
  ShoppingBag,
  Menu,
  ChevronUp,
  ChevronDown
} from 'lucide-react';

const DetailNavbar = () => {
  const [categories, setCategories] = useState([]);
  const [isSideOpen, setIsSideOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [shopName, setShopName] = useState('');
  const [openMenus, setOpenMenus] = useState({});
  const [topNotice, setTopNotice] = useState(null);
  const [noticeLoading, setNoticeLoading] = useState(true);

  const { cartCount, fetchCartCount, clearCart } = useCart();
  const { user, logout } = useAuth();
  const { shopId, setShopId } = useShop();

  const [hideNavbar, setHideNavbar] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const urlShopId = searchParams.get('shop_id') || '';
  const currentType = searchParams.get('type') || '';
  const currentShopId = urlShopId || shopId || '';

  const currentCategoryId = location.pathname.startsWith('/category/')
    ? location.pathname.split('/category/')[1]?.split('/')[0]
    : '';

  const isWholeActive = location.pathname === '/shop';

  const categoryLink = (catId) => {
    const params = new URLSearchParams();

    if (currentShopId) params.set('shop_id', currentShopId);
    if (currentType) params.set('type', currentType);

    const queryString = params.toString();
    return queryString ? `/category/${catId}?${queryString}` : `/category/${catId}`;
  };

  useEffect(() => {
    let lastScrollY = window.scrollY;
  
    const isMobile = () => window.innerWidth <= 767.98;
  
    const handleScroll = () => {
      if (!isMobile()) {
        setHideNavbar(false);
        return;
      }
  
      const currentScrollY = window.scrollY;
  
      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setHideNavbar(true);
      } else {
        setHideNavbar(false);
      }
  
      lastScrollY = currentScrollY;
    };
  
    const handleResize = () => {
      if (!isMobile()) {
        setHideNavbar(false);
      }
    };
  
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
  
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (urlShopId && String(urlShopId) !== String(shopId || '')) {
      setShopId(String(urlShopId));
    }
  }, [urlShopId, shopId, setShopId]);

  useEffect(() => {
    axios.get('/api/platform-home?type=pickup')
      .then(res => {
        setTopNotice(res.data.top_notice || null);
      })
      .finally(() => {
        setNoticeLoading(false);
      });
  }, []);

  useEffect(() => {
    let categoryUrl = '/api/categories';
    let shopInfoUrl = '/api/shop-info';

    if (currentShopId) {
      categoryUrl += `?shop_id=${currentShopId}`;
      shopInfoUrl += `?shop_id=${currentShopId}`;
    }

    axios.get(categoryUrl, { withCredentials: true })
      .then(res => setCategories(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error('카테고리 조회 실패:', err);
        setCategories([]);
      });

    axios.get(shopInfoUrl, { withCredentials: true })
      .then(res => {
        setShopName(res?.data?.shop_name || '');
      })
      .catch(err => {
        console.error('상점 정보 조회 실패:', err);
        setShopName('');
      });

  }, [currentShopId]);

  useEffect(() => {
    if (user) {
      fetchCartCount();
    } else {
      clearCart();
    }
  }, [user, fetchCartCount, clearCart]);

  const handleLogout = async () => {
    if (!window.confirm("로그아웃 하시겠습니까?")) return;
    try {
      await axios.post('/api/auth/logout', {}, { withCredentials: true });
      logout();
      setIsSideOpen(false);
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  };

  const rootCategories = categories.filter(cat => !cat.parent_id);

  const getChildCategories = (parentId) => {
    return categories.filter(cat => String(cat.parent_id) === String(parentId));
  };

  const renderDesktopCategories = () => {
    return rootCategories.map(parent => {
      const children = getChildCategories(parent.id);

      return (
        <div
          key={parent.id}
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            height: '60px'
          }}
          className="pc-category-item"
        >
          <Link
            to={categoryLink(parent.id)}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {parent.name}
          </Link>

          {children.length > 0 && (
            <div className="pc-submenu">
              {children.map(child => (
                <Link
                  key={child.id}
                  to={categoryLink(child.id)}
                  style={{
                    textDecoration: 'none',
                    color: '#333',
                    display: 'block',
                    padding: '8px 12px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {child.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    });
  };

  const toggleSideMenu = (parentId) => {
    setOpenMenus(prev => ({
      ...prev,
      [parentId]: !prev[parentId]
    }));
  };

  return (
    <>
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css" />

      <style>{`
        .notice-strip {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;

          height: 28px;

          background: #eeeeee;
          color: #fff;

          display: flex;
          align-items: center;
          justify-content: center;

          font-size: 12px;
          font-weight: 600;

          z-index: 1200;
        }
        .top-bar {
          background-color: #fff;
          padding: 0.76rem;
          border-bottom: 1px solid #dee2e6;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-weight: bold;
          font-size: 1.2rem;
          position: fixed;
          top: 28px; left: 0; right: 0;
          z-index: 1100;
          height: 60px;
        }
        .top-bar .logo {
          display: flex;
          align-items: center;
          min-width: 0;
        }
        .top-bar .logo a {
          color: #000;
          text-decoration: none;
          font-weight: bold;
        }
        .shop-title {
          display: inline-block;
          min-width: 1px;
          color: #000;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .pc-menu {
          display: flex;
          gap: 1.5rem;
          font-size: 0.95rem;
          font-weight: 600;
        }
        .pc-category-item:hover .pc-submenu {
          display: block;
        }
        .pc-submenu {
          display: none;
          position: absolute;
          top: 88px;
          left: 0;
          background: #fff;
          border: 1px solid #eee;
          min-width: 180px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.08);
          z-index: 1200;
          padding: 6px 0;
        }
        .icons {
          display: flex;
          gap: 1.1rem;
          font-size: 1.5rem;
          align-items: center;
          position: relative;
        }

        .icons svg {
          color: #222;
          cursor: pointer;
          display: block;
        }

        .cart-wrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
        }
        .badge-danger {
          position: absolute;
          top: -2px;
          right: -8px;
          background-color: #dc3545;
          color: white;
          font-size: 10px;
          font-weight: bold;
          border-radius: 50%;
          min-width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        #sideMenu {
          position: fixed;
          top: 0;
          left: -260px;
          width: 260px;
          height: 100vh;
          background: #fff;
          box-shadow: 2px 0 8px rgba(0,0,0,0.15);
          z-index: 2000;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          transition: left 0.3s ease;
          overflow-y: auto;
        }
        #sideMenu.show { left: 0; }

        .side-overlay {
          position: fixed;
          top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0,0,0,0.5);
          z-index: 1500;
        }

        .side-sub-list {
          list-style: none;
          padding-left: 14px;
          margin: 8px 0 0 0;
        }

        .side-sub-list li {
          padding: 8px 0 4px 0;
        }

        .side-parent-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .side-parent-link {
          text-decoration: none;
          color: #333;
          display: block;
          font-weight: 600;
          flex: 1;
        }

        .side-toggle-btn {
          border: none;
          background: none;
          color: #666;
          font-size: 0.95rem;
          cursor: pointer;
          padding: 2px 4px;
          line-height: 1;
        }

        @media (max-width: 767.98px) {
          .navbar-hidden .notice-strip {
            transform: translateY(-28px);
          }

          .navbar-hidden .top-bar {
            transform: translateY(-88px);
          }

          .navbar-hidden .mobile-category-scroll {
            transform: translateY(-124px);
          }
        }

        .notice-strip,
        .top-bar,
        .mobile-category-scroll {
          transition: transform 0.25s ease;
        }

        @media (max-width: 767.98px) {
          .pc-only { display: none !important; }
          #menuIcon { display: inline-block !important; }
        }
        @media (min-width: 768px) {
          .pc-only { display: flex !important; }
          #menuIcon { display: none !important; }
        }

        .mobile-category-scroll {
          display: flex;
          justify-content: space-evenly;
          overflow-x: auto;
        }

        .navbar-spacer {
          height: 88px;
        }

        @media (max-width: 767.98px) {
          .navbar-spacer {
            height: 124px;
          }
        }

        @media (max-width: 767.98px) {
          .mobile-category-scroll {
            position: fixed;
            top: 88px;
            left: 0;
            right: 0;

            height: 36px;
            padding: 0 14px;
            gap: 28px;

            background: #fff;
            border-bottom: 1px solid #e5e5e5;
            display: flex;
            align-items: center;
            overflow-x: auto;
            overflow-y: hidden;
            white-space: nowrap;
            z-index: 1050;
            -webkit-overflow-scrolling: touch;
          }

          .mobile-category-scroll::-webkit-scrollbar {
            display: none;
          }

          .mobile-category-link {
            flex: 0 0 auto;
            min-width: max-content;
            height: 36px;
            position: relative;
            display: flex;
            align-items: center;
            text-decoration: none;
            color: #222;
            font-size: 14px;
            font-weight: 600;
          }

          .mobile-category-link,
          .mobile-category-link:hover,
          .mobile-category-link:focus,
          .mobile-category-link:active,
          .mobile-category-link.active {
            text-decoration: none !important;
          }

          .mobile-category-link.active {
            color: #000;
            font-weight: 800;
          }

          .mobile-category-link.active::after {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 2px;
            background: #000;
          }
        }

        @media (min-width: 768px) {
          .mobile-category-scroll {
            display: none !important;
          }
        }

        @media (max-width: 767.98px) {
          .top-bar {
            border-bottom: 0;
          }
        }
      `}</style>
  <div className={hideNavbar ? 'navbar-hidden' : ''}>
      <div className="notice-strip">
        {topNotice && (
          <Link to={`/notice/${topNotice.id}`} style={{ color:'#000', textDecoration:'none' }}>
            {topNotice.title}
          </Link>
        )}
      </div>

      <div className="top-bar">
        <div className="logo">
          <Link
            to="/"
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
              gap: "3px"
            }}
          >
            <span style={{ fontWeight: "800" }}>네리네</span>
          </Link>
        </div>

        <div className="pc-menu pc-only">
          {renderDesktopCategories()}
        </div>

        <div className="icons">
          <User
            size={20}
            strokeWidth={1.7}
            onClick={() => navigate('/mypage')}
          />

          <Link to="/wishlist" className="cart-wrapper" style={{ textDecoration: 'none', color: 'inherit' }}>
            <Heart
              size={20}
              strokeWidth={1.7}
            />
          </Link>

          <Link to="/cart" className="cart-wrapper" style={{ textDecoration: 'none', color: 'inherit' }}>
            <ShoppingBag
              size={20}
              strokeWidth={1.7}
            />
            {cartCount > 0 && <span className="badge-danger">{cartCount}</span>}
          </Link>

          <Menu
            id="menuIcon"
            size={20}
            strokeWidth={1.7}
            onClick={() => setIsSideOpen(true)}
          />
        </div>

      </div>

      <div className="mobile-category-scroll">
        <Link
          to={`/shop?shop_id=${currentShopId}&type=${currentType || 'pickup'}`}
          className={`mobile-category-link ${isWholeActive ? 'active' : ''}`}
        >
          전체
        </Link>

        {rootCategories.map(parent => (
          <Link
            key={parent.id}
            to={categoryLink(parent.id)}
            className={`mobile-category-link ${String(currentCategoryId) === String(parent.id) ? 'active' : ''}`}
          >
            {parent.name}
          </Link>
        ))}
      </div>
  </div>

      <nav id="sideMenu" className={isSideOpen ? 'show' : ''}>
        <div style={{ textAlign:'right' }}>
          <span style={{ fontSize:'2rem', cursor:'pointer', color:'#666' }} onClick={() => setIsSideOpen(false)}>&times;</span>
        </div>

        <div style={{ marginBottom:'1rem', fontWeight:'600' }}>
          {user ? <span>{user.nickname}님 환영합니다.</span> : <span>방문자님 환영합니다.</span>}
        </div>

        <div style={{ marginBottom:'1rem', fontSize:'0.9rem' }}>
          {user ? 
            <span style={{ color:'#007bff', cursor:'pointer' }} onClick={handleLogout}>로그아웃</span> : 
            <Link to="/login" onClick={() => setIsSideOpen(false)} style={{ textDecoration:'none', color:'#007bff' }}>로그인</Link>
          }
          <span style={{ margin:'0 8px', color:'#ccc' }}>|</span>
          <Link to="/mypage" onClick={() => setIsSideOpen(false)} style={{ textDecoration:'none', color:'#007bff' }}>마이페이지</Link>
        </div>

        <ul style={{ listStyle:'none', padding:0, margin:0 }}>
          {rootCategories.map(parent => {
            const children = getChildCategories(parent.id);
            const isOpen = !!openMenus[parent.id];

            return (
              <li key={parent.id} style={{ padding:'12px 0', borderBottom:'1px solid #eee' }}>
                <div className="side-parent-row">
                  <Link
                    to={categoryLink(parent.id)}
                    onClick={() => setIsSideOpen(false)}
                    className="side-parent-link"
                  >
                    {parent.name}
                  </Link>

                  {children.length > 0 && (
                    <button
                      type="button"
                      className="side-toggle-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleSideMenu(parent.id);
                      }}
                    >
                      <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </button>
                  )}
                </div>

                {children.length > 0 && isOpen && (
                  <ul className="side-sub-list">
                    {children.map(child => (
                      <li key={child.id}>
                        <Link
                          to={categoryLink(child.id)}
                          onClick={() => setIsSideOpen(false)}
                          style={{ textDecoration:'none', color:'#666', display:'block', fontSize:'0.95rem' }}
                        >
                          - {child.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {isSideOpen && <div className="side-overlay" onClick={() => setIsSideOpen(false)}></div>}

      <div className="navbar-spacer" />
    </>
  );
};

export default DetailNavbar;